import React, { useState, useEffect, useRef } from 'react';
import { Button, IconButton, Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import textFit from 'textfit';

/**
 * A React component that renders a Standard 16AIS component.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.text - The text to display on the button. Defaults to '16 digital I/Os, 16 Analog Inputs'.
 * @param {string} props.variant - The button variant. Defaults to 'outlined'.
 * @param {string} props.color - The button color. Defaults to 'primary'.
 * @param {Function} props.onClick - The callback function to call when the button is clicked.
 * @param {Function} props.handleStdioDecrement - The callback function to call when the decrement button is clicked.
 * @param {boolean} props.reset - Whether to reset the count to 0.
 */
const Standard16Ais = ({
    text,
    variant,
    color,
    onClick,
    handleStdioDecrement,
    reset
  }) => {
    /**
     * The count state, initialized to 0.
     */
    const [count, setCount] = useState(0);
  
    /**
     * A reference to the text element.
     */
    const textRef = useRef(null);
  
    /**
     * Resets the count to 0 when the reset prop changes.
     */
    useEffect(() => {
      if (reset) {
        setCount(0);
      }
    }, [reset]);
  
    /**
     * Fits the text to the button width when the text or count changes.
     */
    useEffect(() => {
      if (textRef.current) {
        textFit(textRef.current, { alignVert: false, alignHoriz: true, multiLine: false, maxFontSize: 16 });
      }
    }, [text, count]);
  
    /**
     * Handles the increment button click.
     * 
     * @returns {void}
     */
    const handleIncrement = () => {
      const added = onClick('16 Analog Inputs');
      if (added) {
        setCount(prevCount => prevCount + 1);
      }
    };
  
    /**
     * Handles the decrement button click.
     * 
     * @returns {void}
     */
    const handleDecrement = () => {
      if (count > 0) {
        setCount(prevCount => prevCount - 1);
        handleStdioDecrement(); 
      }
    };
  
    return (
      <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
        <Button
          size="small"
          variant={variant || 'outlined'}
          color={color || 'primary'}
          onClick={handleIncrement}
          sx={{
            color: '#363636',
            width: '100%',
            height: '50px',
            padding: '10px 20px',
            margin: '0 10px',
            textTransform: 'none',
            whiteSpace: 'normal',  
            wordWrap: 'break-word', 
            overflow: 'hidden'  
          }}
        >
          <span ref={textRef}>{text || '16 digital I/Os, 16 Analog Inputs'}</span>
        </Button>
  
        <Typography
          variant="body1"
          sx={{
            fontSize: '16px',
            minWidth: '30px',
            textAlign: 'center',
            '@media (max-width: 600px)': {
              fontSize: '14px',
            }
          }}
        >
          {count}
        </Typography>
  
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <IconButton
            onClick={handleIncrement}
            sx={{
              border: '1px solid #363636',
              borderRadius: '50%',
              padding: '5px'
            }}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            onClick={handleDecrement}
            sx={{
              border: '1px solid #363636',
              borderRadius: '50%',
              padding: '5px'
            }}
          >
            <RemoveIcon />
          </IconButton>
        </div>
      </Box>
    );
  };
  
  /**
   * Exports the Standard16Ais component as the default export.
   */
  export default Standard16Ais;